import { SceneService } from '@src/services/SceneService';

import { VideoEditorLogger } from '../../utility/VideoEditorLogger';
import { PunchListTrackManager } from '../lib/PunchListTrackManager';
import { Scene } from '../models/layout';
import { AbstractCommand, TimelineMode } from './index';

import type { Element } from '../models';
import type { VideoTranscriptElement } from '../models/elements/VideoTranscriptElement';
import type { VideoEditor } from '../VideoEditor';

/**
 * Command to shift multiple elements by a time offset
 */
export class ShiftMultipleElementsCommand extends AbstractCommand {
  private elementIds: string[];

  getName(): string {
    return 'ShiftMultipleElementsCommand';
  }
  private timeShift: number;
  private snapToFrame: boolean;
  private executed: boolean = false;
  private transcriptTrackModified: boolean = false;
  private punchListElementIds: string[] = [];
  private _shouldCreateUndoRedoEntry: boolean = true;
  private readonly sceneService = new SceneService();

  constructor(
    editor: VideoEditor,
    elementIds: string[],
    timeShift: number,
    snapToFrame: boolean = false,
    shouldCreateUndoRedoEntry: boolean = true,
  ) {
    super(editor, undefined);
    this.elementIds = elementIds;
    this.timeShift = timeShift;
    this.snapToFrame = snapToFrame;
    this._shouldCreateUndoRedoEntry = shouldCreateUndoRedoEntry;
  }

  shouldCreateUndoRedoEntry(): boolean {
    return this._shouldCreateUndoRedoEntry;
  }

  execute(): boolean {
    VideoEditorLogger.logCommand(this, 'start', {
      elementIds: this.elementIds,
      timeShift: this.timeShift,
      timelineMode: this.getTimelineMode(),
    });

    try {
      this.storeAffectedElements();
    } catch (error) {
      return false;
    }

    if (!this.shiftAffectedElements()) {
      return false;
    }

    this.executed = true;

    // Recalculate punch list positions if any punch list elements were shifted
    if (this.punchListElementIds.length > 0) {
      const punchListTrackManager = new PunchListTrackManager(this.editor);
      punchListTrackManager.recalculatePunchListPosition(
        this.punchListElementIds,
      );
    }
    this.recalculateTimeline();

    VideoEditorLogger.logCommand(this, 'complete', {
      elementIds: this.elementIds,
      timeShift: this.timeShift,
    });

    return true;
  }

  /**
   * Checks if the element is a VideoTranscriptElement
   */
  private isTranscriptTrackElement(
    element: Element,
  ): element is VideoTranscriptElement {
    return (
      this.editor
        .getTranscriptTrack()
        ?.elements?.some(
          (el): el is VideoTranscriptElement => el.id === element.id,
        ) ?? false
    );
  }

  private shiftAffectedElements(): boolean {
    for (const affectedElement of this.affectedElements) {
      const { element } = affectedElement;
      const newStartTime = element.time + this.timeShift;
      let success;
      if (this.isTranscriptTrackElement(element)) {
        this.transcriptTrackModified = true;
        success = this.shiftTranscriptElement(element, newStartTime);
      } else {
        success = this.shiftRegularElement(element, newStartTime);
      }

      if (!success) {
        VideoEditorLogger.logCommand(this, 'error', {
          error: 'Failed to shift element',
          elementId: element.id,
          timeShift: this.timeShift,
        });
        return false;
      }
    }

    // Validate all scenes after all shifts complete
    if (this.transcriptTrackModified) {
      this.sceneService.validateAndAdjustScenes(this.editor.timeline);
    }

    return true;
  }

  /**
   * Shifts a transcript track element and handles scene adjustments
   */
  private shiftTranscriptElement(
    element: VideoTranscriptElement,
    newStartTime: number,
  ): boolean {
    const track = this.editor.getVideoTranscriptTrack();

    // Capture scene context BEFORE shift
    const prevElement = track?.getPreviousVideoTranscriptElement(element.id);
    const nextElement = track?.getNextVideoTranscriptElement(element.id);
    const capturedScenes = this.sceneService.captureOverlappingScenes(
      this.editor.timeline,
      element,
      element.time,
      element.end_time,
      element.getMediaStartTime(),
      element.getMediaEndTime(),
    );

    const shiftResult = this.editor
      .getTranscriptTrackManager()
      .shiftElement(
        element,
        newStartTime,
        TimelineMode.DEFAULT,
        this.snapToFrame,
      );

    // Handle scene adjustment for cut_paste operations
    if (
      shiftResult !== false &&
      shiftResult.result === 'cut_paste' &&
      shiftResult.pasted
    ) {
      this.adjustScenesAfterCutPaste(
        element,
        shiftResult.pasted,
        prevElement,
        nextElement,
        capturedScenes,
      );
    }

    return shiftResult !== false;
  }

  /**
   * Shifts a regular (non-transcript) element
   */
  private shiftRegularElement(element: Element, newStartTime: number): boolean {
    const trackManager = this.editor.getTrackManager();
    if (trackManager.isPunchListElement(element.id)) {
      this.punchListElementIds.push(element.id);
    }
    return trackManager.shiftElement(
      element,
      newStartTime,
      TimelineMode.DEFAULT,
      undefined,
      this.snapToFrame,
    );
  }

  /**
   * Adjusts scenes after a cut_paste operation on a transcript element
   */
  private adjustScenesAfterCutPaste(
    originalElement: VideoTranscriptElement,
    pastedElement: VideoTranscriptElement,
    prevElement: VideoTranscriptElement | null | undefined,
    nextElement: VideoTranscriptElement | null | undefined,
    capturedScenes: Scene[],
  ): void {
    this.sceneService.adjustScenesAfterDelete(this.editor.timeline, {
      deletedElementId: originalElement.id,
      previousElementId: prevElement?.id ?? null,
      previousElementMediaEnd: prevElement?.getMediaEndTime() ?? null,
      nextElementId: nextElement?.id ?? null,
      nextElementMediaStart: nextElement?.getMediaStartTime() ?? null,
    });

    capturedScenes.forEach(scene => {
      const newStartAnchor = this.sceneService.retargetAnchor(
        scene.anchorStart,
        pastedElement.id,
      );
      const newEndAnchor = this.sceneService.retargetAnchor(
        scene.anchorEnd,
        pastedElement.id,
      );
      this.sceneService.addScene(
        this.editor.timeline,
        new Scene({
          anchorStart: newStartAnchor,
          anchorEnd: newEndAnchor,
          layout: scene.layout,
        }),
      );
    });
  }

  undo(): boolean {
    if (!this.executed) {
      return false;
    }

    this.restoreAffectedElements();
    this.recalculateTimeline();
    return true;
  }

  getDescription(): string {
    return `Shift multiple elements (${
      this.elementIds.length
    }) by ${this.timeShift.toFixed(2)}s`;
  }

  protected storeAffectedElements(): void {
    this.affectedElements = this.elementIds
      .map(id => {
        const element = this.editor.getElementById(id);
        if (!element) {
          VideoEditorLogger.logCommand(this, 'error', {
            error: 'Failed to store affected elements: element not found',
            elementId: id,
            timeShift: this.timeShift,
          });
          throw new Error(`Element with ID ${id} not found`);
        }
        return element;
      })
      .map(element => ({
        element,
        originalState: this.captureElementState(element),
      }));
    // Need to ensure the order of operation, so that there are no conflicts:
    // e.g. first element cannot be shifted to the right because the second
    // affected element is in the way. To prevent such situation we should move
    // the rightmost element first (and vice versa for left direction).
    if (this.timeShift > 0) {
      this.affectedElements.sort((a, b) => b.element.time - a.element.time);
    } else {
      this.affectedElements.sort((a, b) => a.element.time - b.element.time);
    }
  }

  protected restoreAffectedElements(): void {
    this.affectedElements.forEach(({ element, originalState }) => {
      this.restoreElementState(element, originalState);
    });
  }
}
